import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { FormErrorDirective } from '@shared/directives/form-error.directive';

@Component({
  selector: 'app-form-input',
  imports: [ReactiveFormsModule, CommonModule, FormErrorDirective],
  template: ` <div>
    <label [for]="id" class="block text-sm/6 font-medium text-gray-900">{{ label }}</label>
    <div class="">
      <input
        [type]="isCurrency ? 'text' : type"
        [id]="id"
        [formControl]="control"
        [placeholder]="placeholder"
        (input)="onInput($event)"
        (blur)="onBlur()"
        class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400
          focus:outline-1 focus:-outline-offset-1 focus:outline-focus/70 focus:shadow-sm sm:text-sm/6 transition duration-300 ease-in-out disabled:bg-gray-100 disabled:text-gray-500"
          [ngClass]="{ 'outline-red-300 ': hasError }"
      />
      <span appFormError [errors]="errors" [hidden]="!hasError" class="text-xs mt-1 text-red-400"></span>
    </div>
  </div>`,
})
export class FormInputComponent implements OnInit {
  private _control: FormControl = new FormControl();

  @Input() label!: string;
  @Input() id!: string;
  @Input() type: 'text' | 'email' | 'password' | 'number' = 'text';
  @Input() className = '';
  @Input() placeholder = '';
  @Input() isCurrency = false;
  @Input() set control(value: AbstractControl<any, any> | null) {
    this._control = (value as FormControl) ?? new FormControl();
  }
  @Input() set disabled(value: boolean) {
    this.isDisabled = value;
    if (value) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  isDisabled = false;

  get control(): FormControl {
    return this._control;
  }

  get hasError(): boolean {
    return this.control.invalid && this.control.touched;
  }

  get errors(): any {
    return this.control.errors || {};
  }

  ngOnInit(): void {
    if (this.isCurrency) {
      this.formatInitialValue();
    }
  }

  private formatInitialValue(): void {
    const value = this.control.value;
    if (value) {
      const formattedValue = this.formatCurrency(value.toString());
      this.control.setValue(formattedValue, { emitEvent: false });
    }
  }

  onInput(event: Event): void {
    if (!this.isCurrency) return;

    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.value;

    if (value.includes('$')) {
      value = value.replace(/[^\d]/g, '');
    }

    const numericOnly = value.replace(/[^\d]/g, '');

    if (value !== numericOnly) {
      inputElement.value = numericOnly;
      this.control.setValue(numericOnly, { emitEvent: false });
    }
  }

  onBlur(): void {
    if (!this.isCurrency) return;

    const value = this.control.value;
    if (!value) return;

    let numericValue = value.toString();
    if (numericValue.includes('$')) {
      numericValue = numericValue.replace(/[^\d]/g, '');
    }

    if (numericValue) {
      try {
        const formattedValue = this.formatCurrency(numericValue);
        this.control.setValue(formattedValue, { emitEvent: false });
      } catch (error) {
        console.error('Error al formatear valor en blur:', error);
      }
    }
  }

  private formatCurrency(value: string): string {
    if (!value) return '';
    const numericValue = parseInt(value, 10);
    if (isNaN(numericValue)) return '';

    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(numericValue);
  }
}
